import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIS } from "../../../../utils/APIS";
import { GET, POST, PUT, DELETE } from "../../../../utils/Methods";
import { IDS } from "../../../../utils/IDS";

// SSM - Get All (Read)
export const GetAll = createAsyncThunk(
  "Management/SSM/get-all",
  async (filter, { rejectWithValue }) => {
    try {
      const res = await GET(
        `${APIS.Management.SSM}?name=${filter?.name || ""}&page=${
          filter?.page || IDS.PAGE
        }&per_page=${filter?.per_page || IDS.PERPAGE}&email=${
          filter?.email || ""
        }&contact=${filter?.contact || ""}&start_date=${
          filter?.startDate || ""
        }&end_date=${filter?.endDate || ""}&user_id=${filter?.user_id || ""}`,
      );
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// SSM - Get (Read)
export const GetSingle = createAsyncThunk(
  "Management/SSM/get",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.Management.SSM}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// SSM - Create (Add)
export const Create = createAsyncThunk(
  "Management/SSM/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await POST(APIS.Management.SSM, data);

      if (res?.success) {
        return res;
      }
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// SSM - Update
export const Update = createAsyncThunk(
  "Management/SSM/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await PUT(`${APIS.Management.SSM}/${id}`, data);
      if (res?.success) {
        return res;
      }
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// SSM - Delete
export const Delete = createAsyncThunk(
  "Management/SSM/delete",
  async (id, { rejectWithValue }) => {
    try {
      const data = await DELETE(`${APIS.Management.SSM}/${id}`);
      return id; // Return the id of the deleted SSM
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// SSM - Status
export const StatusUpdate = createAsyncThunk(
  "Management/SSM/status",
  async (id, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.Management.SSM}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const handleMoney = createAsyncThunk(
  "Management/SSM/status-wildcard",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await POST(
        `${APIS.Wallet.WithdrawReq}/add-remove-points/${id}`,
        data,
      );
      if (res?.success) return res;
      return rejectWithValue(res);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const Slice = createSlice({
  name: "SSM",
  initialState: {
    data: [],
    current_page: IDS.PAGE,
    total_pages: IDS.TOTALPAGES,
    per_page: IDS.PERPAGE,
    total: IDS.TOTAL,
    status: IDS.SLICESTATUS.Idle,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handling all actions for GetSingle, Create, Update, Delete
      .addCase(GetAll.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAll.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.data = action.payload?.data?.data || [];
        state.current_page = action.payload?.data?.current_page || IDS.PAGE;
        state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
        state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
        state.total = action.payload?.data?.total || IDS.TOTAL;
      })
      .addCase(GetAll.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetSingle.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetSingle.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
      })
      .addCase(GetSingle.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Create.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Create.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data.unshift(action.payload.data);
      })
      .addCase(Create.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Update.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Update.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        const index = state.data.findIndex(
          (SSM) => SSM.id === action.payload?.data?.id,
        );
        if (index !== -1) {
          state.data[index] = action.payload.data;
        }
      })
      .addCase(Update.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Delete.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Delete.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data = state.data.filter((SSM) => SSM.id !== action.payload.id); // Remove the SSM with the deleted id
      })
      .addCase(Delete.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })
      //
      .addCase(StatusUpdate.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(StatusUpdate.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data = state.data.filter((SSM) => SSM.id !== action.payload.id); // Remove the SSM with the deleted id
      })
      .addCase(StatusUpdate.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      });
  },
});

// Export actions (if needed in components)
export const {} = Slice.actions;

export default Slice.reducer;
